import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface AppointmentFormData {
  subject: string;
  date: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  appointmentReasonId: string;
  treatmentAgencyId: string;
  locationId: string;
  contactName?: string;
}

const CURRENT_DATE_TIME_ERROR =
  "The date cannot be earlier than the current date and time.";

export const appointmentValidationSchema: yup.ObjectSchema<AppointmentFormData> =
  yup
    .object({
      subject: yup
        .string()
        .trim()
        .required("Reason for appointment is required"),
      date: yup
        .mixed<Dayjs>()
        .nullable()
        .test(
          "is-valid-date",
          "Date is required",
          (value) => !!value && dayjs(value).isValid()
        )
        .test(
          "is-after-release-date",
          "Appointments must start after the participant's release date",
          function (value) {
            // Eligibility begins the DAY AFTER release: the release date itself and every
            // earlier date are ineligible, because the participant is still in custody. The
            // server enforces this too; catching it here puts the correction beside the field
            // instead of after a save round-trip.
            //
            // Supplied through the resolver context rather than the form, since the release date
            // belongs to the enrolment and is not one of this form's fields.
            const releaseDate = this?.options?.context?.releaseDate;
            if (!releaseDate || !value) return true;
            return dayjs(value)
              .startOf("day")
              .isAfter(dayjs(releaseDate as string).startOf("day"));
          }
        )
        .required("Date is required"),
      startTime: yup
        .mixed<Dayjs>()
        .nullable()
        .test(
          "is-valid-start",
          "Start time is required",
          (value) => !!value && dayjs(value).isValid()
        )
        .test("is-not-past-today", CURRENT_DATE_TIME_ERROR, function (value) {
          const skipPastValidation = this?.options?.context?.skipPastValidation;
          if (skipPastValidation) return true;
          const { date } = this.parent as AppointmentFormData;
          if (!value || !date) return true;
          const startDateTime = dayjs(date)
            .hour(dayjs(value).hour())
            .minute(dayjs(value).minute())
            .second(0)
            .millisecond(0)
            .utcOffset(0, true); // interpret as naive UTC (same wall time with Z)
          const now = dayjs().utcOffset(0, true);
          return startDateTime.isAfter(now);
        })
        .required("Start time is required"),
      endTime: yup
        .mixed<Dayjs>()
        .nullable()
        .test(
          "is-valid-end",
          "End time is required",
          (value) => !!value && dayjs(value).isValid()
        )
        .required("End time is required")
        .test(
          "is-after-start",
          "End time must be after start time",
          function (value) {
            const { startTime, date } = this.parent as AppointmentFormData;
            if (!value || !startTime || !date) return true;
            const startDateTime = dayjs
              .utc(date)
              .hour(dayjs(startTime).hour())
              .minute(dayjs(startTime).minute());
            const endDateTime = dayjs
              .utc(date)
              .hour(dayjs(value).hour())
              .minute(dayjs(value).minute());
            return endDateTime.isAfter(startDateTime);
          }
        )
        .test("is-not-past-today", CURRENT_DATE_TIME_ERROR, function (value) {
          const skipPastValidation = this?.options?.context?.skipPastValidation;
          if (skipPastValidation) return true;
          const { date } = this.parent as AppointmentFormData;
          if (!value || !date) return true;
          const endDateTime = dayjs(date)
            .hour(dayjs(value).hour())
            .minute(dayjs(value).minute())
            .second(0)
            .millisecond(0)
            .utcOffset(0, true); // interpret as naive UTC (same wall time with Z)
          const now = dayjs().utcOffset(0, true);

          // A half-typed date makes endDateTime invalid, and toISOString() then throws
          // "RangeError: Invalid time value" -- an UNCAUGHT error that CRA's dev overlay puts over
          // the whole page, and that in production surfaces as a crash while the coordinator is
          // still typing. This was a leftover debug console.log doing exactly that; removed rather
          // than guarded, since it should not be logging on every keystroke either.
          if (!endDateTime.isValid()) return true;

          return endDateTime.isAfter(now);
        }),
      appointmentReasonId: yup.string().trim().required("Reason is required"),
      treatmentAgencyId: yup
        .string()
        .trim()
        .required("Treatment Agency is required"),
      locationId: yup.string().trim().required("Address is required"),
      contactName: yup.string().trim().optional(),
    })
    .required();

export default appointmentValidationSchema;
